import { useSyncExternalStore } from 'react';
import { useFeatureFlagEnabled } from '@posthog/react';
import posthog from 'posthog-js';

import { useIsClient } from '~/hooks/useIsClient';

import { type FeatureFlagKey } from './featureFlags';
import { isPostHogEnabled } from './initPostHog';

export type FeatureFlagState = 'loading' | 'enabled' | 'disabled';

/** PostHog boolean flag as `'loading' | 'enabled' | 'disabled'`. See `docs/posthog-feature-flags.md`. */
export function useFeatureFlagState(flagKey: FeatureFlagKey): FeatureFlagState {
  const isEnabled = useFeatureFlagEnabled(flagKey);
  const flagsLoaded = useFeatureFlagsLoaded();
  const isClient = useIsClient();

  if (!isClient) {
    return 'loading';
  }

  if (!isPostHogEnabled()) {
    return import.meta.env.DEV ? 'enabled' : 'disabled';
  }

  if (!flagsLoaded) {
    return 'loading';
  }

  return isEnabled ? 'enabled' : 'disabled';
}

function subscribeFeatureFlags(onStoreChange: () => void) {
  if (!isPostHogEnabled()) {
    return () => {};
  }

  return posthog.onFeatureFlags(onStoreChange);
}

function getFeatureFlagsLoadedSnapshot() {
  if (!isPostHogEnabled()) {
    return false;
  }

  return Boolean(posthog.featureFlags?.hasLoadedFlags);
}

function useFeatureFlagsLoaded(): boolean {
  return useSyncExternalStore(subscribeFeatureFlags, getFeatureFlagsLoadedSnapshot, () => false);
}
